import React from 'react';
import styled from 'styled-components';
import { DonationForm } from '../../types';

interface EmbeddedDonationFormProps {
  config: DonationForm['embedded'];
  className?: string;
}

const EmbedContainer = styled.div<{ height: string }>`
  position: relative;
  overflow: hidden;
  height: ${props => props.height};
  width: 100%;
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: ${props => props.theme.shadows.sm};
  background: ${props => props.theme.colors.background};
`;

const EmbedIframe = styled.iframe`
  position: absolute;
  border: 0;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 100%;
  border-radius: inherit;
`;

const LoadingOverlay = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.colors.neutral[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  border-radius: inherit;
  z-index: 1;

  &::before {
    content: '';
    width: 40px;
    height: 40px;
    border: 3px solid ${props => props.theme.colors.neutral[300]};
    border-top: 3px solid ${props => props.theme.colors.primary[500]};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  padding: ${props => props.theme.spacing[6]};
  text-align: center;
  background: ${props => props.theme.colors.neutral[50]};
  border: 2px dashed ${props => props.theme.colors.neutral[300]};
  border-radius: ${props => props.theme.borderRadius.lg};
  color: ${props => props.theme.colors.neutral[600]};
  
  h3 {
    margin-bottom: ${props => props.theme.spacing[2]};
    color: ${props => props.theme.colors.text};
  }
`;

const EmbeddedDonationForm: React.FC<EmbeddedDonationFormProps> = ({ config, className }) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);

  const handleLoad = React.useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleError = React.useCallback(() => {
    setIsLoading(false);
    setHasError(true);
  }, []);

  if (hasError) {
    return (
      <ErrorMessage className={className}>
        <h3>Unable to load donation form</h3>
        <p>Please try refreshing the page or contact us directly to make a donation.</p>
      </ErrorMessage>
    );
  }

  return (
    <EmbedContainer height={config.height} className={className}>
      <LoadingOverlay isVisible={isLoading} />
      <EmbedIframe
        src={config.url}
        title={config.title}
        allowTransparency={config.allowTransparency}
        {...(config.allowPaymentRequest && { allowPaymentRequest: true })}
        onLoad={handleLoad}
        onError={handleError}
        loading="lazy"
        sandbox="allow-scripts allow-forms allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      />
    </EmbedContainer>
  );
};

export default EmbeddedDonationForm;